// hooks/useProductCategorySearch.js
//
// Debounced server-side typeahead against the product-category picker
// endpoints. Backs <ProductCategoryPicker>.
//
// Two source modes, picked via the `scopeToStore` option:
//
//   - false (default)   → GET /api/product-categories/
//                         Everything the merchant may ASSIGN to an Item
//                         (ItemSetup, DataHealth missing-category fix,
//                         Add Item). What that IS depends on the store's
//                         `category_source`: a taxonomy store gets the
//                         full Shopify taxonomy (any category, used or
//                         not); a PRODUCT_TYPE store gets its own derived
//                         product-type nodes — the endpoint decides (see
//                         ProductCategoryViewSet.get_queryset).
//
//   - true              → GET /api/product-categories/in-store/
//                         Only categories assigned to at least one Item in
//                         the current store — no parent categories. Used for
//                         TARGETING (promotions, report filters, chart
//                         group/filter dropdowns). Picking a category
//                         the store has zero items in is incoherent for
//                         targeting; this mode hides those.
//
// BOTH modes cache per storeId: neither endpoint returns the same rows for
// two stores once `category_source` is in play.
//
// Cached by term + scope: the same search fragment returns the same result
// until the taxonomy or an item/category write changes it.
//
// This module owns the FETCHING (endpoint, wire shape, cache keys). The
// grid-cell wiring built on top of it lives in utils/categoryPickerColDef,
// which reads its pages through `fetchProductCategoryWindow` below.

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiJson } from '../queries/api';
import { productCategories as productCategoriesKey } from '../queries/keys';

export const STALE_MS = 5 * 60 * 1000;   // 5 min — reference data
const MIN_CHARS_FOR_SEARCH = 2;

export const PAGE_SIZE = 50;

/**
 * Fetch one page of the category typeahead, returning the rows plus the total
 * match count. `count` powers AG Grid Rich Select's infinite paging
 * (`valuesPage` → `lastRow`); both endpoints paginate
 * (`ProductCategoryPagination`), so the body is `{count, results}`.
 *
 * @returns {Promise<{values: Array<{id,name,display_label}>, count: number}>}
 */
export async function fetchProductCategoriesPage(
  searchTerm, page = 1, pageSize = PAGE_SIZE, { scopeToStore = false } = {},
) {
  const trimmed = (searchTerm ?? '').trim();
  const url = scopeToStore
    ? '/api/product-categories/in-store/'
    : '/api/product-categories/';
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (trimmed) params.set('search', trimmed);
  const body = await apiJson.get(`${url}?${params.toString()}`);
  return { values: body.results, count: body.count };
}

/**
 * Fetch the first page (≤50) of the category typeahead as a bare array —
 * what the MUI `<ProductCategoryPicker>` Autocomplete consumes. Shares the
 * endpoint/wire-shape with the paged fetcher above.
 *
 * @returns {Promise<Array<{id,name,display_label}>>}
 */
export async function fetchProductCategories(searchTerm, opts = {}) {
  const { values } = await fetchProductCategoriesPage(searchTerm, 1, PAGE_SIZE, opts);
  return values;
}

/**
 * Fetch-and-cache ONE backend page for a search term, keyed per
 * (storeId, term, page) so pages scrolled-in stay warm and a repeat open is
 * free. The unit both grid pickers page through.
 *
 * @returns {Promise<{values: Array<object>, count: number}>}
 */
export function fetchProductCategoryPage(queryClient, storeId, term, page) {
  return queryClient.fetchQuery({
    queryKey: productCategoriesKey.searchPage(storeId, term, page),
    queryFn: () => fetchProductCategoriesPage(term, page, PAGE_SIZE),
    staleTime: STALE_MS,
  });
}

/**
 * Fetch EXACTLY the [startRow, endRow) window AG Grid's Rich Select asks for
 * (its documented `valuesPage` contract). The backend is page-indexed
 * (PageNumberPagination) and a window can straddle two pages whenever a
 * non-aligned startRow is used — e.g. if a caller adds
 * valuesPageInitialStartRow to scroll-to-value — so every page the window
 * overlaps is fetched and sliced to the precise range. `count` (total matches)
 * also doubles as the editor's `lastRow`.
 *
 * Page 1 is always read first because it carries `count`, and `count` is what
 * bounds every other page request: DRF 404s a page past the end, and a caller
 * that adds rows of its own to the list (the product-type picker's "create"
 * row) makes the editor ask for a window that starts there. The editor pages
 * sequentially from row 0, so after the first window this is a cache hit.
 *
 * @returns {Promise<{values: Array<object>, count: number}>}
 */
export async function fetchProductCategoryWindow(queryClient, storeId, term, startRow, endRow) {
  const first = await fetchProductCategoryPage(queryClient, storeId, term, 1);
  const { count } = first;
  const firstPage = Math.floor(startRow / PAGE_SIZE) + 1;
  const lastPageInData = Math.max(1, Math.ceil(count / PAGE_SIZE));
  if (firstPage > lastPageInData) return { values: [], count };
  const lastPage = Math.min(lastPageInData, Math.floor((endRow - 1) / PAGE_SIZE) + 1);

  const byPage = new Map([[1, first.values]]);
  for (let page = firstPage; page <= lastPage; page++) {
    if (byPage.has(page)) continue;
    byPage.set(page, (await fetchProductCategoryPage(queryClient, storeId, term, page)).values);
  }

  const values = [];
  for (let page = firstPage; page <= lastPage; page++) {
    const pageStart = (page - 1) * PAGE_SIZE;
    const pv = byPage.get(page) || [];
    for (let i = 0; i < pv.length; i++) {
      const abs = pageStart + i;
      if (abs >= startRow && abs < endRow) values.push(pv[i]);
    }
  }
  return { values, count };
}

export function useProductCategorySearch(searchTerm, { scopeToStore = false } = {}) {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const trimmed = (searchTerm ?? '').trim();

  // Empty / short input: load the first page of the alphabetical tree so
  // the picker has SOMETHING to show on open. Two characters or more:
  // server does a contains-match across display_label / name / full_name.
  // BOTH modes require a resolved storeId (auth bootstrap may not have
  // finished yet) — the rows are store-dependent either way, so caching a
  // response under `{storeId: undefined}` would leak it to the next store.
  const termOk = trimmed.length === 0 || trimmed.length >= MIN_CHARS_FOR_SEARCH;
  const enabled = termOk && !!storeId;

  const queryKey = scopeToStore
    ? productCategoriesKey.inStoreSearch(storeId, trimmed)
    : productCategoriesKey.search(storeId, trimmed);

  const query = useQuery({
    queryKey,
    queryFn: () => fetchProductCategories(trimmed, { scopeToStore }),
    staleTime: STALE_MS,
    enabled,
  });

  return {
    categories: query.data ?? [],
    loading: query.isPending && enabled,
    error: query.error,
  };
}
